"use client"

import type { ReactNode } from "react"
import { useTranslations } from "next-intl"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { BloodMembershipComparison } from "@/components/ui/blood-membership-comparison"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import {
  BiomarkerHeroSection,
  BiomarkerDetailCardSection,
  BiomarkerFactsSection,
  BiomarkerGapSection,
  BiomarkerRisksSection,
  BiomarkerActionSection,
  BiomarkerClosingSection,
  LinkedCopy,
} from "@/components/ui/biomarker-landing"
import { BiomarkerStepsWalkthrough } from "@/components/ui/biomarker-steps-walkthrough"
import { cortisolConfig } from "./cortisol-config"

export default function CortisolClient({
  testimonials,
  faq,
  priceFrom,
}: {
  /** Streamed CMS sections — async server components in `<Suspense>`,
   *  provided by the page so a slow CMS delays only the section. */
  testimonials: ReactNode
  faq: ReactNode
  priceFrom?: string
}) {
  const lp = useLocalePath()
  const t = useTranslations("go")
  const config = cortisolConfig(
    t,
    lp("/what-we-detect"),
    lp("/tests/advanced-blood-test"),
    priceFrom
  )
  const { biomarkerPanel } = config

  return (
    <>
      <BiomarkerHeroSection config={config} />
      <BiomarkerDetailCardSection config={config} />
      <BiomarkerFactsSection config={config} />
      <BiomarkerRisksSection config={config} />
      <BiomarkerGapSection config={config} />
      <BiomarkerActionSection config={config} />
      <BiomarkerStepsWalkthrough config={config} />
      {/* Biomarker panel (FH-style) — makes "part of the blood test" concrete */}
      <section className="scroll-mt-24 bg-neutral-50 py-12 md:py-16">
        <BiomarkerDiseaseExplorer
          packages={["blood", "addon-single"]}
          // Payload doc id for cortisol (locale-invariant). Do NOT use the slug
          // — it's localized (`cortisol-level` in en, `cortisol-morning` in de).
          addonIds={["190"]}
          tabsAllowed={["biomarkers"]}
          diseaseContentEnabled={false}
          isBloodTest
          eyebrow={biomarkerPanel.eyebrow}
          headingSizeClass="type-display-sm"
          biomarkersTab={{
            heading: biomarkerPanel.heading,
            subheading: (
              <LinkedCopy
                text={biomarkerPanel.subheading}
                phrase="Advanced blood test"
                href={config.advancedBloodTestHref}
              />
            ),
            cta: {
              label: biomarkerPanel.ctaLabel,
              href: lp("/biomarkers"),
              variant: "text",
            },
          }}
        />
      </section>
      {/* Real social proof (CMS testimonials) */}
      {testimonials}
      {/* Pricing — add-on framing: Advanced Blood Test (with cortisol) vs membership */}
      <div className="py-8 md:py-12">
        <BloodMembershipComparison />
      </div>
      {faq}
      <BiomarkerClosingSection config={config} />
    </>
  )
}
